/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * DealAlerts.tsx — Full Part 3
 * Complete deal alert system:
 * - Alert creation with Claude evaluation, market triggers, digest mode
 * - Match review dashboard with AI scores, review/flag, add to pipeline
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

const MSAS = [
  'Atlanta, GA', 'Charlotte, NC', 'Dallas, TX', 'Denver, CO',
  'Seattle, WA', 'Kansas City, MO', 'Las Vegas, NV', 'Tampa, FL',
  'Washington, D.C.', 'Los Angeles, CA',
];

interface Alert {
  id: number; name: string; msas: string[];
  min_units: number | null; max_units: number | null;
  min_price: number | null; max_price: number | null;
  keywords: string | null; is_active: boolean;
  created_at: string; last_triggered: string | null;
  notify_digest: boolean;
  min_cap_rate: number | null; max_cap_rate: number | null;
}

interface Match {
  id: number; alert_id: number; title: string; url: string;
  source: string; match_reason: string; ai_score: number;
  ai_analysis: string | null; is_reviewed: boolean; is_flagged: boolean;
  pipeline_deal_id: number | null; matched_at: string; alert_name: string;
}

interface DealAlertsProps { token: string; }

const DealAlerts: React.FC<DealAlertsProps> = ({ token }) => {
  const gold = '#b8956a'; const goldDim = 'rgba(201,169,110,0.08)';
  const green = '#1A7A4A'; const red = '#CC2200'; const bg = '#ffffff';
  const surface = '#fafaf8'; const surfaceRaised = '#f2ede6'; const surfaceHigh = '#e6ddd4';
  const border = '#e6ddd4'; const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c'; const textSecondary = '#12526e'; const textMuted = '#2d7fa0';
  const serifFont = "'Fraunces', Georgia, serif";
  const headers = { Authorization: `Bearer ${token}` };

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [unreviewed, setUnreviewed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'alerts' | 'matches'>('alerts');
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [matchFilter, setMatchFilter] = useState<'all' | 'unreviewed' | 'flagged'>('all');
  const [addingToPipeline, setAddingToPipeline] = useState<number | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [selectedMsas, setSelectedMsas] = useState<string[]>([]);
  const [minUnits, setMinUnits] = useState('');
  const [maxUnits, setMaxUnits] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [keywords, setKeywords] = useState('');
  const [notifyDigest, setNotifyDigest] = useState(false);
  const [minCapRate, setMinCapRate] = useState('');
  const [maxCapRate, setMaxCapRate] = useState('');
  const [formError, setFormError] = useState('');

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/terminal/alerts/`, { headers });
      setAlerts(res.data.alerts || []);
      setMatches(res.data.matches || []);
      setUnreviewed(res.data.unreviewed_count || 0);
    } catch {}
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);

  const resetForm = () => {
    setName(''); setSelectedMsas([]); setMinUnits(''); setMaxUnits('');
    setMinPrice(''); setMaxPrice(''); setKeywords('');
    setNotifyDigest(false); setMinCapRate(''); setMaxCapRate('');
    setFormError('');
  };

  const handleCreate = async () => {
    if (!name.trim()) { setFormError('Alert name is required.'); return; }
    setCreating(true); setFormError('');
    try {
      await axios.post(`${API}/terminal/alerts/`, {
        name: name.trim(), msas: selectedMsas,
        min_units: minUnits ? parseInt(minUnits) : null,
        max_units: maxUnits ? parseInt(maxUnits) : null,
        min_price: minPrice ? parseFloat(minPrice) * 1e6 : null,
        max_price: maxPrice ? parseFloat(maxPrice) * 1e6 : null,
        keywords: keywords.trim() || null,
        notify_digest: notifyDigest,
        min_cap_rate: minCapRate ? parseFloat(minCapRate) : null,
        max_cap_rate: maxCapRate ? parseFloat(maxCapRate) : null,
      }, { headers });
      setShowForm(false); resetForm(); await fetch();
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || 'Failed to create alert.');
    } finally { setCreating(false); }
  };

  const handleToggle = async (id: number) => {
    try {
      await axios.patch(`${API}/terminal/alerts/${id}/toggle`, {}, { headers });
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_active: !a.is_active } : a));
    } catch {}
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API}/terminal/alerts/${id}`, { headers });
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch {}
  };

  const handleReview = async (matchId: number, reviewed: boolean, flagged?: boolean) => {
    try {
      await axios.patch(`${API}/terminal/alerts/matches/${matchId}/review`, {
        is_reviewed: reviewed,
        ...(flagged !== undefined && { is_flagged: flagged }),
      }, { headers });
      setMatches(prev => prev.map(m => m.id === matchId
        ? { ...m, is_reviewed: reviewed, ...(flagged !== undefined && { is_flagged: flagged }) }
        : m
      ));
      if (reviewed) setUnreviewed(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleAddToPipeline = async (matchId: number, title: string) => {
    setAddingToPipeline(matchId);
    try {
      const res = await axios.post(`${API}/terminal/alerts/matches/${matchId}/add-to-pipeline`, {
        match_id: matchId,
        deal_name: title.slice(0, 80),
        pipeline_stage: 'Prospecting',
      }, { headers });
      setMatches(prev => prev.map(m =>
        m.id === matchId ? { ...m, pipeline_deal_id: res.data.deal_id, is_reviewed: true } : m
      ));
    } catch {}
    finally { setAddingToPipeline(null); }
  };

  const scoreColor = (s: number) => s >= 70 ? green : s >= 50 ? gold : textMuted;
  const scoreLabel = (s: number) => s >= 70 ? 'High' : s >= 50 ? 'Medium' : 'Low';
  const relTime = (d: string) => {
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const filteredMatches = matches.filter(m => {
    if (matchFilter === 'unreviewed') return !m.is_reviewed;
    if (matchFilter === 'flagged') return m.is_flagged;
    return true;
  });

  const inp: React.CSSProperties = {
    backgroundColor: '#f2ede6', border: `1px solid ${borderLight}`,
    padding: '9px 12px', color: textPrimary, fontSize: '12px',
    outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, backgroundColor: '#ffffff', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Intelligent Monitoring</div>
            <div style={{ fontSize: '20px', fontWeight: 400, color: textPrimary, fontFamily: serifFont }}>Deal Alerts</div>
          </div>
          <button onClick={() => { setShowForm(v => !v); if (showForm) resetForm(); }}
            style={{ backgroundColor: showForm ? 'transparent' : gold, color: showForm ? textSecondary : bg, border: showForm ? `1px solid ${borderLight}` : 'none', padding: '8px 18px', fontSize: '10px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            {showForm ? 'Cancel' : '+ New Alert'}
          </button>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: '0', marginTop: '12px' }}>
          {(['alerts', 'matches'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '6px 16px', border: 'none', borderBottom: `2px solid ${tab === t ? gold : 'transparent'}`, cursor: 'pointer', fontSize: '9px', fontWeight: tab === t ? 700 : 500, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'inherit', backgroundColor: 'transparent', color: tab === t ? gold : textMuted }}>
              {t === 'matches' ? `Matches${unreviewed > 0 ? ` (${unreviewed} new)` : ''}` : 'Alerts'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* Create form */}
        {showForm && (
          <div style={{ backgroundColor: '#ffffff', borderBottom: `1px solid ${border}`, padding: '20px' }}>
            <div style={{ fontSize: '10px', color: gold, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '16px' }}>Configure Alert</div>
            {formError && <div style={{ padding: '8px 12px', marginBottom: '12px', backgroundColor: 'rgba(204,34,0,0.08)', border: `1px solid rgba(204,34,0,0.25)`, color: red, fontSize: '11px' }}>{formError}</div>}

            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>Alert Name *</div>
              <input style={inp} placeholder="e.g. 100-300 unit Sun Belt value-add" value={name} onChange={e => setName(e.target.value)} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>Target Markets</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {MSAS.map(msa => {
                  const sel = selectedMsas.includes(msa);
                  return (
                    <button key={msa} onClick={() => setSelectedMsas(prev => sel ? prev.filter(m => m !== msa) : [...prev, msa])}
                      style={{ padding: '4px 10px', fontSize: '10px', cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${sel ? gold : border}`, backgroundColor: sel ? goldDim : 'transparent', color: sel ? gold : textMuted }}>
                      {msa.split(',')[0]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
              {[
                { label: 'Min Units', val: minUnits, set: setMinUnits, ph: '100' },
                { label: 'Max Units', val: maxUnits, set: setMaxUnits, ph: '500' },
                { label: 'Min Price ($M)', val: minPrice, set: setMinPrice, ph: '20' },
                { label: 'Max Price ($M)', val: maxPrice, set: setMaxPrice, ph: '150' },
              ].map(({ label, val, set, ph }) => (
                <div key={label}>
                  <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>{label}</div>
                  <input style={inp} type="number" placeholder={ph} value={val} onChange={e => set(e.target.value)} />
                </div>
              ))}
            </div>

            {/* Market triggers */}
            <div style={{ marginBottom: '14px', backgroundColor: '#fafaf8', border: `1px solid ${border}`, padding: '12px' }}>
              <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px' }}>Market Triggers (optional)</div>
              <div style={{ fontSize: '10px', color: textMuted, marginBottom: '10px' }}>Fire when cap rate in target markets crosses a threshold — no article required.</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>Alert when cap rate ≥</div>
                  <input style={inp} type="number" step="0.1" placeholder="e.g. 5.5" value={minCapRate} onChange={e => setMinCapRate(e.target.value)} />
                </div>
                <div>
                  <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>Alert when cap rate ≤</div>
                  <input style={inp} type="number" step="0.1" placeholder="e.g. 4.0" value={maxCapRate} onChange={e => setMaxCapRate(e.target.value)} />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>Keywords</div>
              <input style={inp} placeholder="e.g. value-add, garden-style, workforce" value={keywords} onChange={e => setKeywords(e.target.value)} />
            </div>

            {/* Notification mode */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '8px' }}>
              {[{ val: false, label: 'Immediate Email', desc: 'Email when score ≥ 60' }, { val: true, label: 'Daily Digest', desc: 'One email per day at 8am' }].map(opt => (
                <div key={String(opt.val)} onClick={() => setNotifyDigest(opt.val)}
                  style={{ flex: 1, padding: '10px', border: `1px solid ${notifyDigest === opt.val ? gold : border}`, backgroundColor: notifyDigest === opt.val ? goldDim : 'transparent', cursor: 'pointer' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: notifyDigest === opt.val ? gold : textSecondary, marginBottom: '2px' }}>{opt.label}</div>
                  <div style={{ fontSize: '9px', color: textMuted }}>{opt.desc}</div>
                </div>
              ))}
            </div>

            <button onClick={handleCreate} disabled={creating || !name.trim()}
              style={{ width: '100%', padding: '11px', border: 'none', backgroundColor: name.trim() && !creating ? gold : surfaceHigh, color: name.trim() && !creating ? bg : textMuted, fontSize: '11px', fontWeight: 800, cursor: name.trim() && !creating ? 'pointer' : 'default', fontFamily: 'inherit', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              {creating ? 'Creating...' : 'Create Alert'}
            </button>
          </div>
        )}

        {/* Alerts tab */}
        {tab === 'alerts' && !showForm && (
          <>
            {loading ? (
              <div style={{ padding: '20px', fontSize: '11px', color: textMuted, letterSpacing: '1px', textTransform: 'uppercase' }}>Loading...</div>
            ) : alerts.length === 0 ? (
              <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 300, color: textPrimary, fontFamily: serifFont, marginBottom: '8px' }}>No alerts configured</div>
                <div style={{ fontSize: '12px', color: textMuted, marginBottom: '20px', lineHeight: 1.6 }}>
                  Create alerts to monitor deal activity, market conditions,<br />and get notified when opportunities match your criteria.
                </div>
                <button onClick={() => setShowForm(true)}
                  style={{ backgroundColor: gold, color: bg, border: 'none', padding: '10px 24px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                  Create First Alert
                </button>
              </div>
            ) : alerts.map(alert => (
              <div key={alert.id} style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, display: 'flex', gap: '12px', opacity: alert.is_active ? 1 : 0.5 }}>
                <div style={{ width: '3px', alignSelf: 'stretch', backgroundColor: alert.is_active ? green : textMuted, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: textPrimary }}>{alert.name}</div>
                    <span style={{ fontSize: '8px', fontWeight: 800, color: alert.is_active ? green : textMuted, letterSpacing: '1px', textTransform: 'uppercase' }}>{alert.is_active ? 'ACTIVE' : 'PAUSED'}</span>
                    {alert.notify_digest && <span style={{ fontSize: '8px', color: textMuted, border: `1px solid ${border}`, padding: '1px 5px' }}>DIGEST</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    {alert.msas.length > 0 && <span style={{ fontSize: '10px', color: textSecondary }}>📍 {alert.msas.map(m => m.split(',')[0]).join(', ')}</span>}
                    {(alert.min_units || alert.max_units) && <span style={{ fontSize: '10px', color: textSecondary }}>🏢 {alert.min_units || '0'}–{alert.max_units || '∞'} units</span>}
                    {(alert.min_price || alert.max_price) && <span style={{ fontSize: '10px', color: textSecondary }}>💰 ${(alert.min_price || 0) / 1e6}M–{alert.max_price ? `$${alert.max_price / 1e6}M` : '∞'}</span>}
                    {alert.keywords && <span style={{ fontSize: '10px', color: textSecondary }}>🔍 {alert.keywords}</span>}
                    {alert.min_cap_rate && <span style={{ fontSize: '10px', color: gold }}>📊 Cap ≥ {alert.min_cap_rate}%</span>}
                    {alert.max_cap_rate && <span style={{ fontSize: '10px', color: gold }}>📊 Cap ≤ {alert.max_cap_rate}%</span>}
                  </div>
                  {alert.last_triggered && <div style={{ fontSize: '9px', color: gold }}>Last match: {relTime(alert.last_triggered)}</div>}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => handleToggle(alert.id)}
                    style={{ backgroundColor: 'transparent', border: `1px solid ${borderLight}`, color: textSecondary, padding: '4px 10px', fontSize: '9px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    {alert.is_active ? 'Pause' : 'Resume'}
                  </button>
                  <button onClick={() => handleDelete(alert.id)}
                    style={{ backgroundColor: 'transparent', border: `1px solid rgba(204,34,0,0.3)`, color: red, padding: '4px 8px', fontSize: '9px', cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Matches tab */}
        {tab === 'matches' && (
          <>
            {/* Filter bar */}
            <div style={{ display: 'flex', gap: '0', borderBottom: `1px solid ${border}`, padding: '0 20px', backgroundColor: '#fafaf8' }}>
              {(['all', 'unreviewed', 'flagged'] as const).map(f => (
                <button key={f} onClick={() => setMatchFilter(f)}
                  style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${matchFilter === f ? gold : 'transparent'}`, cursor: 'pointer', fontSize: '9px', fontWeight: matchFilter === f ? 700 : 500, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'inherit', backgroundColor: 'transparent', color: matchFilter === f ? gold : textMuted }}>
                  {f === 'unreviewed' && unreviewed > 0 ? `Unreviewed (${unreviewed})` : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', fontSize: '9px', color: textMuted }}>
                {filteredMatches.length} matches
              </div>
            </div>

            {filteredMatches.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: '14px', color: textMuted, fontFamily: serifFont }}>No matches yet</div>
                <div style={{ fontSize: '11px', color: textMuted, marginTop: '8px' }}>
                  Coast monitors news every 30 minutes. Matches appear here with AI confidence scores.
                </div>
              </div>
            ) : (
              filteredMatches.map(match => (
                <div key={match.id} style={{ borderBottom: `1px solid ${border}`, padding: '14px 20px', backgroundColor: match.is_reviewed ? 'transparent' : 'rgba(201,169,110,0.02)', opacity: match.is_reviewed && !match.is_flagged ? 0.7 : 1 }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {/* AI Score */}
                    <div style={{ flexShrink: 0, textAlign: 'center', minWidth: '44px' }}>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: scoreColor(match.ai_score), fontFamily: serifFont, lineHeight: 1 }}>{match.ai_score}</div>
                      <div style={{ fontSize: '8px', color: scoreColor(match.ai_score), fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{scoreLabel(match.ai_score)}</div>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '8px', color: gold, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>{match.alert_name}</span>
                        {match.is_flagged && <span style={{ fontSize: '8px', color: gold }}>⚑ FLAGGED</span>}
                        {match.pipeline_deal_id && <span style={{ fontSize: '8px', color: green, fontWeight: 700 }}>✓ IN PIPELINE</span>}
                        <span style={{ fontSize: '9px', color: textMuted, marginLeft: 'auto' }}>{relTime(match.matched_at)}</span>
                      </div>

                      <a href={match.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '13px', fontWeight: 600, color: textPrimary, fontFamily: serifFont, textDecoration: 'none', lineHeight: 1.4, display: 'block', marginBottom: '6px' }}>
                        {match.title}
                      </a>

                      {match.ai_analysis && (
                        <div style={{ fontSize: '11px', color: textSecondary, lineHeight: 1.5, marginBottom: '8px', padding: '8px', backgroundColor: '#fafaf8', borderLeft: `2px solid ${border}` }}>
                          {match.ai_analysis}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '9px', color: gold, fontWeight: 700 }}>{match.source}</span>
                        <span style={{ fontSize: '9px', color: textMuted }}>·</span>
                        <span style={{ fontSize: '9px', color: textMuted }}>{match.match_reason}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px', paddingLeft: '56px' }}>
                    {!match.is_reviewed && (
                      <button onClick={() => handleReview(match.id, true)}
                        style={{ backgroundColor: 'transparent', border: `1px solid ${borderLight}`, color: textSecondary, padding: '4px 10px', fontSize: '9px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        Mark Reviewed
                      </button>
                    )}
                    <button onClick={() => handleReview(match.id, match.is_reviewed, !match.is_flagged)}
                      style={{ backgroundColor: match.is_flagged ? 'rgba(201,169,110,0.1)' : 'transparent', border: `1px solid ${match.is_flagged ? gold : borderLight}`, color: match.is_flagged ? gold : textSecondary, padding: '4px 10px', fontSize: '9px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      {match.is_flagged ? '⚑ Unflag' : '⚑ Flag'}
                    </button>
                    {!match.pipeline_deal_id && (
                      <button
                        onClick={() => handleAddToPipeline(match.id, match.title)}
                        disabled={addingToPipeline === match.id}
                        style={{ backgroundColor: addingToPipeline === match.id ? surfaceHigh : green, color: addingToPipeline === match.id ? textMuted : '#fff', border: 'none', padding: '4px 12px', fontSize: '9px', cursor: addingToPipeline === match.id ? 'default' : 'pointer', fontFamily: 'inherit', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        {addingToPipeline === match.id ? 'Adding...' : '+ Pipeline'}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DealAlerts;